/*
 * File: getDriveEnrollmentLabel.ts
 * Author: Austin Robinson
 * Date Created: 4/18/2022
 * Copyright (c) DrivingDoc 2022
 */

/**
 * Returns the display string for a drive period enrollment:
 * - Numeric period number (e.g. "1", "2") if the student attended or attendance not taken
 * - "C" if canceled
 * - "AB" if absent
 * - "NP" if no permit
 * - Empty string if not countable (e.g. future period, deleted)
 *
 * @param enrollment - The period enrollment object with DB-calculated `period_number`
 * @param prepend - Optional string to prepend to the number (e.g. "P" → "P1")
 * @returns Formatted period display string
 *
 * @example
 * ```ts
 * getDriveEnrollmentLabel(enrollment) // → "2"
 * getDriveEnrollmentLabel(enrollment) // → "AB"
 * getDriveEnrollmentLabel(enrollment, 'P') // → "P3"
 * ```
 */
export function getDriveEnrollmentLabel(
  enrollment: {
    period_number: string | number | null;
    canceled_at: string | null;
    attendance: string | null;
  },
  prepend: string = '',
): string {
  // 1. Canceled takes highest priority
  if (enrollment.canceled_at) {
    return 'C';
  }

  // 2. Attendance codes
  if (enrollment.attendance === 'absent') {
    return 'AB';
  }
  if (enrollment.attendance === 'no_permit') {
    return 'NP';
  }

  // 3. Valid counted period
  if (enrollment.period_number !== null && enrollment.period_number !== undefined) {
    return `${prepend}${enrollment.period_number}`;
  }

  // 4. Not countable (e.g. future, deleted, no prior present)
  return '';
}
